/* eslint-disable import/no-cycle */
import {useState} from 'react';
import {Form} from 'antd';

import {observer} from 'mobx-react-lite';
import {useTranslation} from 'react-i18next';
import Alert from 'antd/es/alert';
import moment from 'moment';
import {useMount} from '../../hooks/useMount';
import styles from './InterruptionModal.module.css';
import Modal from '../shared/modal/Modal';
import {useStore} from '../../hooks/useStore';
import {DatePicker} from '../shared/DatePicker';
import Input from '../shared/inputs/Input';
import {ValidationRules} from '../../utils/validationRules';
import Reasons from './Reasons';
import Button from '../shared/buttons/Button';
import {datetimeFormat} from '../../config/moment';

const {TextArea} = Input;

const InterruptionModal = ({
  form,
  onSubmit,
  onCancel,
  currentLog = null,
  ...props
}) => {
  const store = useStore();
  const {t} = useTranslation();

  const interruptionReason = currentLog
    ? store.interruptionReasonStore.getById(currentLog.interruptionReasonId)
    : undefined;

  const [isCommentRequired, setIsCommentRequired] = useState(interruptionReason?.isMessageRequired);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  const latestOperationLog = store.operationStateLogStore
    .getLatestOfWorkplace(store.workplaceStore.selectedWorkplace?.id);
  const latestWorkplaceLog = store.workplaceStateLogStore
    .getLatestOfWorkplace(store.workplaceStore.selectedWorkplace?.id);
  const latestStart = latestOperationLog === undefined || latestWorkplaceLog.start > latestOperationLog.start
    ? latestWorkplaceLog.start
    : latestOperationLog.start;

  useMount(() => {
    if (store.workplaceStore.selectedWorkplace?.id) {
      store.validTransitionsStore.loadValidTransitionsByWorkplace(
        store.workplaceStore.selectedWorkplace.id
      ).then((validTransitions) => {
        if (validTransitions) {
          if (validTransitions.find((vt) => vt.activeOperationToStateId)) {
            store.operationStateStore.loadMany(validTransitions.map((vt) => vt.activeOperationToStateId));
          }
          if (validTransitions.find((vt) => vt.workplaceToStateId)) {
            store.workplaceStateStore.loadMany(validTransitions.map((vt) => vt.workplaceToStateId));
          }
        }
      });
    }
  }, [store.workplaceStore.selectedWorkplace?.id]);

  const validTransitions = store.validTransitionsStore.transitions.filter(
    (transition) => (
      currentLog
        ? !transition.activeOperationToState?.isInterruption && !transition.workplaceToState?.isInterruption
        : transition.activeOperationToState?.isInterruption || transition.workplaceToState?.isInterruption
    )
  );

  const hasMultipleTransitions = validTransitions.length > 1;
  const transition = validTransitions.length > 0 ? validTransitions[0] : undefined;

  const onReasonChange = (value) => {
    const reason = store.interruptionReasonStore.getById(value);
    setIsCommentRequired(reason?.isMessageRequired);
  };

  const onDateChange = () => {
    setTouched(true);
  };

  const handleCancel = () => {
    setTouched(false);
    onCancel();
  };

  const disabledDate = (current) => {
    if (!latestStart) {
      return false;
    }
    const maxStart = moment(latestStart).add(1, 'minute');
    const maxEnd = moment();
    return !(maxStart.isSameOrBefore(current) && maxEnd.isSameOrAfter(current));
  };

  return (
    <Modal visible footer={false} maskClosable={false} onCancel={handleCancel} {...props}>
      {hasMultipleTransitions && (
        <Alert
          className={styles.alertMessage}
          message={t('interruption.modal.alert.hasMultipleTransitions')}
          type="warning"
          closable
          showIcon
        />
      )}
      <Form
        onFinish={(values) => {
          setIsSubmitting(true);
          const data = {transitionId: transition?.id, ...values};
          if (!touched) {
            data.datetime = undefined;
          } else {
            data.datetime = moment(data.datetime).seconds(0);
          }
          onSubmit({transitionId: transition?.id, ...data});
          setTouched(false);
        }}
        initialValues={{
          comment: currentLog?.message,
          reason: currentLog?.interruptionReasonId,
          datetime: moment(),
        }}
        layout={'vertical'}
      >
        <p>{t('interruption.modal.form.datetime.label')}</p>
        <Form.Item name="datetime">
          <DatePicker
            showToday
            showTime
            format={datetimeFormat}
            allowClear={false}
            disabledDate={disabledDate}
            disabled={isSubmitting}
            onChange={onDateChange}
          />
        </Form.Item>
        <Form.Item
          name="comment"
          label={t('interruption.modal.form.comment.label')}
          rules={[ValidationRules.requiredIf(isCommentRequired)]}
        >
          <TextArea
            placeholder={t('interruption.modal.form.comment.placeholder')}
          />
        </Form.Item>
        <Form.Item
          name="reason"
          rules={[ValidationRules.requiredIf(currentLog !== null)]}
          label={t('interruption.modal.form.reason.label')}
        >
          <Reasons onChange={onReasonChange}/>
        </Form.Item>
        <Form.Item className={styles.buttonRow}>
          <Button
            className={styles.buttonDiscard}
            htmlType="button"
            size="large"
            onClick={() => handleCancel()}
            disabled={isSubmitting}
            danger
          >
            {t('interruption.modal.form.button.discard')}
          </Button>
          <Button type="primary" htmlType="submit" size="large" loading={isSubmitting}>
            {t('interruption.modal.form.button.submit')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default observer(InterruptionModal);
